import React from "react";
import * as d3 from "d3";
import "./SpatialGrid.css";
import ColorLegend from "./ColorLegend";
import * as htmlToImage from "html-to-image";

const SpatialGridOverlay = ({ response, selectedVariable }) => {
  // Move all React Hooks to the top level of the component
  const visualizationRef = React.useRef(null);

  // 1. Validate incoming data, similar to your working component
  if (
    !response ||
    !response.data ||
    !response.coords ||
    !response.coords.latitude ||
    !response.coords.longitude ||
    !response.data[selectedVariable]
  ) {
    return <p style={{ color: "gray" }}>No valid data to display.</p>;
  }

  const lats = response.coords.latitude;
  const lons = response.coords.longitude;
  const timeSeries = response.data[selectedVariable];

  if (!Array.isArray(timeSeries)) {
    return <p style={{ color: "red" }}>Invalid time series data.</p>;
  }

  // 2. Extract the latest spatial data slice
  const latestSlice = timeSeries[timeSeries.length - 1]; // Shape: [lat][lon]

  if (!latestSlice || !Array.isArray(latestSlice)) {
    return (
      <p style={{ color: "orange" }}>No numeric data available to visualize.</p>
    );
  }

  // 3. Flatten the data to find min/max values for the color scale
  const allValues = latestSlice
    .flat()
    .filter((value) => typeof value === "number" && !isNaN(value));
  if (allValues.length === 0) {
    return (
      <p style={{ color: "orange" }}>No numeric data available to visualize.</p>
    );
  }

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);

  // 4. Create the color scale
  const colorScale = d3
    .scaleSequential(d3.interpolatePuBuGn)
    .domain([min, max]);

  // 5. Generate the grid cells
  const gridCells = [];
  for (let i = 0; i < lats.length - 1; i++) {
    for (let j = 0; j < lons.length - 1; j++) {
      const rawValue = latestSlice[i]?.[j];
      const value = typeof rawValue === "number" ? rawValue : Number(rawValue);

      if (!isFinite(value)) continue;

      gridCells.push(
        <div
          key={`${i}-${j}`}
          className="grid-cell"
          style={{
            backgroundColor: colorScale(value),
            opacity: 0.8,
          }}
        >
          <div className="tooltip-content">
            <strong>Value:</strong> {value.toFixed(2)} <br />
            Lat: {lats[i].toFixed(2)}°<br />
            Lon: {lons[j].toFixed(2)}°
          </div>
        </div>
      );
    }
  }

  const gridContainerStyle = {
    display: "grid",
    gridTemplateColumns: `repeat(${lons.length - 1}, 1fr)`,
    gridTemplateRows: `repeat(${lats.length - 1}, 1fr)`,
    width: "100%",
    height: "100%",
  };

  const handleDownload = () => {
    if (visualizationRef.current) {
      htmlToImage
        .toPng(visualizationRef.current, { backgroundColor: "#f0f2f5" })
        .then(function (dataUrl) {
          const link = document.createElement("a");
          link.download = "spatial-grid-chart.png";
          link.href = dataUrl;
          link.click();
        })
        .catch(function (error) {
          console.error("oops, something went wrong!", error);
        });
    }
  };

  return (
    <>
      <button className="download-button" onClick={handleDownload}>
        ⬇ Download Chart
      </button>
      <div className="visualization-container" ref={visualizationRef}>
        <div className="spatial-grid-container" style={gridContainerStyle}>
          {gridCells}
        </div>
        <ColorLegend min={min} max={max} colorScale={colorScale} />
      </div>
    </>
  );
};

export default SpatialGridOverlay;
